import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/site/Reveal";
import { Sprig } from "@/components/site/Logo";

const FEES = [
  {
    service: "Individual therapy",
    session: "$200 per 50-minute session",
    funding: "Medicare rebate of $89.50 with an eligible referral; private payment or eligible third-party funding",
  },
  {
    service: "Play therapy",
    session: "$200 per 50-minute session",
    funding: "Private payment, Medicare rebate of $89.50 with an eligible referral, or NDIS where applicable",
  },
  {
    service: "EMDR",
    session: "$200 per 50-minute session",
    funding: "Medicare rebate of $89.50 with an eligible referral; private payment; NDIS where applicable",
  },
  {
    service: "Couples counselling",
    session: "$220 per 60-minute session",
    funding: "Usually privately funded",
  },
  {
    service: "Family therapy",
    session: "$220 per 60-minute session",
    funding: "Funding depends on participants and referral",
  },
  {
    service: "Veteran support",
    session: "50-minute session",
    funding: "No out-of-pocket cost with an Open Arms referral; other arrangements where eligible",
  },
  {
    service: "NSW Victims Assist therapy",
    session: "50-minute session",
    funding: "Fully funded through NSW Victims Services for approved applicants — no out-of-pocket cost",
  },
  {
    service: "NDIS Positive Behaviour Support",
    session: "NDIS rate or service agreement",
    funding: "Capacity Building — Improved Relationships",
  },
  {
    service: "Family law reports",
    session: "$4,200 + GST",
    funding: "Usually privately or legally funded",
  },
  {
    service: "Clinical supervision",
    session: "$220 per session (GST inclusive) — individual or group",
    funding: "Privately funded; may be tax-deductible as professional development",
  },
  {
    service: "Employee Assistance Program",
    session: "Per employer agreement",
    funding: "Employer-funded — no cost to the employee",
  },
];

const NOTES = [
  {
    title: "Medicare",
    text: "With a Mental Health Treatment Plan and referral from your GP, psychiatrist or paediatrician, a Medicare rebate of $89.50 per session is available for eligible individual therapy sessions with an Accredited Mental Health Social Worker.",
  },
  {
    title: "Private health",
    text: "Some private health funds offer rebates for social work services. Check with your fund to confirm your level of cover — we provide receipts for claiming.",
  },
  {
    title: "NDIS service agreements",
    text: "Positive Behaviour Support is funded under Capacity Building — Improved Relationships. A service agreement outlining scope, rates and timeframes is completed before work begins. Self-managed and plan-managed participants are welcome.",
  },
  {
    title: "Cancellation policy",
    text: "We ask for at least 48 hours notice to change or cancel an appointment. A cancellation fee may apply for late notice or non-attendance.",
  },
  {
    title: "Reports & court attendance",
    text: "Report writing, file review and court attendance are charged separately from session fees and are quoted in advance. Court attendance attracts an additional fee.",
  },
  {
    title: "Payment methods",
    text: "Payment is accepted by bank transfer or card. Fees are payable on the day of your appointment unless otherwise arranged in your service agreement.",
  },
  {
    title: "Open Arms & Victims Assist",
    text: "We accept Open Arms referrals for current and former ADF members, veterans and defence families, and clients approved through NSW Victims Services (Victims Assist). Any funding arrangement is confirmed with you before your first appointment.",
  },
];

export default function FeesFunding() {
  return (
    <main data-testid="fees-page">
      <section className="relative overflow-hidden bg-white">
        <div className="pointer-events-none absolute -right-10 -top-10 text-euca-badge">
          <Sprig className="h-64 w-64" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 relative">
          <SectionHeading
            eyebrow="Fees & Funding"
            title="Transparent fees, flexible funding"
            lead="Fees depend on the service, session length and funding arrangement. The table below gives a quick overview — contact the practice to confirm current fees for your situation."
          />
        </div>
      </section>

      <section className="bg-euca-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <Reveal>
            <div className="overflow-x-auto rounded-2xl border border-euca-line bg-white shadow-sm" data-testid="fees-table">
              <table className="w-full min-w-[720px] text-left">
                <thead>
                  <tr className="border-b border-euca-line bg-euca-dark text-euca-mist">
                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-[0.2em]">Service</th>
                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-[0.2em]">Session or assessment</th>
                    <th className="px-6 py-4 text-xs font-semibold uppercase tracking-[0.2em]">Funding information</th>
                  </tr>
                </thead>
                <tbody>
                  {FEES.map((row) => (
                    <tr key={row.service} className="border-b border-euca-line last:border-0 transition-colors hover:bg-euca-mist/50">
                      <td className="px-6 py-5 font-serif text-lg font-medium text-euca-dark">{row.service}</td>
                      <td className="px-6 py-5 text-base text-charcoal/85">{row.session}</td>
                      <td className="px-6 py-5 text-base text-mutedsoft">{row.funding}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {NOTES.map((n, i) => (
              <Reveal key={n.title} delay={(i % 3) * 0.08}>
                <div className="h-full rounded-2xl border border-euca-line bg-white p-7 shadow-sm">
                  <h3 className="font-serif text-xl font-medium text-euca-dark">{n.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-mutedsoft">{n.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-14 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="fees-enquire-button"
                className="inline-flex items-center gap-2 rounded-full bg-euca px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-euca-dark"
              >
                Ask about fees <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
